import React, { useState, useRef, useCallback, useEffect } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  MapPin, Trash2, X, Search, Loader2, Plus, Pencil, ImagePlus,
  AlertTriangle, Eye, EyeOff,
} from "lucide-react";
import { useSelector } from "react-redux";
import {
  useGetRoadBlocksQuery,
  useCreateRoadBlockMutation,
  useUpdateRoadBlockMutation,
  useDeleteRoadBlockMutation,
  useUploadImagesMutation,
} from "../store/api/adminApi";
import { useToast } from "../components/Toast";

const MAPBOX_TOKEN =
  "pk.eyJ1IjoidmlnNzcxOWFudG8iLCJhIjoiY21wMTg0a2t2MDJmNTJzb2x6MDFyazEwMSJ9.UkauDvpPrPSqymE795UYkA";
const MAP_STYLE = "mapbox://styles/mapbox/streets-v12";
const DEFAULT_CENTER = { lat: 12.1696, lng: -68.2837 };

/** Reverse-geocode a point to a human address via the Mapbox Geocoding API. */
async function reverseGeocode(lng, lat) {
  const fallback = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  try {
    const url =
      `https://api.mapbox.com/search/geocode/v6/reverse?longitude=${lng}&latitude=${lat}` +
      `&limit=1&access_token=${MAPBOX_TOKEN}`;
    const res = await fetch(url);
    if (!res.ok) return fallback;
    const data = await res.json();
    const f = data?.features?.[0];
    return f?.properties?.full_address || f?.properties?.name || fallback;
  } catch {
    return fallback;
  }
}

/** Forward-geocode a search query, biased toward Bonaire. */
async function forwardGeocode(query) {
  try {
    const url =
      `https://api.mapbox.com/search/geocode/v6/forward?q=${encodeURIComponent(query)}` +
      `&limit=5&proximity=${DEFAULT_CENTER.lng},${DEFAULT_CENTER.lat}` +
      `&access_token=${MAPBOX_TOKEN}`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    return (data?.features || [])
      .map((f) => ({
        id: f.id,
        name: f.properties?.name || f.properties?.full_address || "Unnamed",
        address: f.properties?.full_address || f.properties?.place_formatted || "",
        lng: f.geometry?.coordinates?.[0],
        lat: f.geometry?.coordinates?.[1],
      }))
      .filter((r) => r.lng != null && r.lat != null);
  } catch {
    return [];
  }
}

/* ─── Location picker modal (Mapbox GL) ─── */
function LocationPickerModal({ initial, onClose, onConfirm }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const pickPointRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [selected, setSelected] = useState(
    initial?.lat != null ? { lat: initial.lat, lng: initial.lng } : null,
  );
  const [address, setAddress] = useState(initial?.address || "");
  const [loadingAddress, setLoadingAddress] = useState(false);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const pickPoint = useCallback(async (lng, lat, knownAddress) => {
    setSelected({ lat, lng });
    setResults([]);
    const map = mapRef.current;
    if (map) {
      if (markerRef.current) {
        markerRef.current.setLngLat([lng, lat]);
      } else {
        const m = new mapboxgl.Marker({ color: "#e11d48", draggable: true })
          .setLngLat([lng, lat])
          .addTo(map);
        m.on("dragend", () => {
          const p = m.getLngLat();
          pickPointRef.current?.(p.lng, p.lat);
        });
        markerRef.current = m;
      }
    }
    if (knownAddress) {
      setAddress(knownAddress);
      return;
    }
    setLoadingAddress(true);
    const addr = await reverseGeocode(lng, lat);
    setAddress(addr);
    setLoadingAddress(false);
  }, []);
  pickPointRef.current = pickPoint;

  useEffect(() => {
    if (!mapContainerRef.current) return undefined;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const start = selected || DEFAULT_CENTER;
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLE,
      center: [start.lng, start.lat],
      zoom: selected ? 15 : 11,
    });
    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    map.on("click", (e) => pickPointRef.current?.(e.lngLat.lng, e.lngLat.lat));
    map.on("load", () => {
      setReady(true);
      if (selected) {
        const m = new mapboxgl.Marker({ color: "#e11d48", draggable: true })
          .setLngLat([selected.lng, selected.lat])
          .addTo(map);
        m.on("dragend", () => {
          const p = m.getLngLat();
          pickPointRef.current?.(p.lng, p.lat);
        });
        markerRef.current = m;
      }
    });
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setResults(await forwardGeocode(query.trim()));
    setSearching(false);
  };

  const chooseResult = (r) => {
    setQuery(r.name);
    setResults([]);
    if (mapRef.current) mapRef.current.flyTo({ center: [r.lng, r.lat], zoom: 15 });
    pickPoint(r.lng, r.lat, r.address || r.name);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[680px] max-w-[95vw] overflow-hidden flex flex-col" style={{ height: 520 }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-[15px] font-manrope-bold text-gray-900">Pin the blocked location</h2>
            <p className="text-[11px] font-manrope-regular text-gray-400 mt-0.5">
              Search or click the map to set where the road is blocked
            </p>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all">
            <X size={15} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 relative">
          <div className="absolute top-3 left-3 z-10 w-[280px]">
            <form onSubmit={runSearch} className="relative">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search a place or address"
                className="w-full pl-9 pr-3 py-2 bg-white rounded-xl shadow-md text-[12px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none border border-gray-100"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                {searching ? (
                  <Loader2 size={13} className="animate-spin text-gray-400" />
                ) : (
                  <Search size={13} className="text-gray-400" />
                )}
              </div>
            </form>
            {results.length > 0 && (
              <div className="mt-1.5 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                {results.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => chooseResult(r)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
                  >
                    <p className="text-[12px] font-manrope-semibold text-gray-800 truncate">{r.name}</p>
                    {r.address && (
                      <p className="text-[10px] font-manrope-regular text-gray-400 truncate">{r.address}</p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div ref={mapContainerRef} className="w-full h-full" />

          {!ready && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 gap-2">
              <Loader2 size={18} className="animate-spin text-gray-400" />
              <p className="text-[13px] font-manrope-regular text-gray-400">Loading map…</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 flex-shrink-0 bg-white">
          <div className="flex-1 mr-4 min-w-0">
            {selected ? (
              <div>
                <p className="text-[11px] font-manrope-semibold text-gray-400 mb-0.5">Selected location</p>
                <p className="text-[12px] font-manrope-regular text-gray-700 truncate">
                  {loadingAddress ? "Resolving address…" : address}
                </p>
                <p className="text-[10px] font-manrope-regular text-gray-400 mt-0.5">
                  Lat: {selected.lat.toFixed(6)} · Lng: {selected.lng.toFixed(6)}
                </p>
              </div>
            ) : (
              <p className="text-[12px] font-manrope-regular text-gray-400">Click on the map to drop a pin</p>
            )}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={onClose} className="px-4 py-2 rounded-xl border border-gray-200 text-[12px] font-manrope-semibold text-gray-600 hover:bg-gray-50 transition-all">
              Cancel
            </button>
            <button
              onClick={() => { if (selected) { onConfirm({ ...selected, address }); onClose(); } }}
              disabled={!selected}
              className="px-4 py-2 rounded-xl bg-primary text-white text-[12px] font-manrope-semibold hover:opacity-90 disabled:opacity-40 transition-all"
            >
              Confirm location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Delete confirm modal ─── */
function DeleteModal({ block, onCancel, onConfirm, isDeleting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl p-8 w-[340px] flex flex-col items-center text-center shadow-2xl">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
          <Trash2 size={28} className="text-primary" strokeWidth={1.8} />
        </div>
        <h3 className="text-[16px] font-manrope-bold text-primary mb-2">Delete road block?</h3>
        <p className="text-[12px] font-manrope-regular text-gray-400 mb-1 leading-relaxed">
          You are about to delete{" "}
          <span className="font-manrope-semibold text-gray-700">{block?.title}</span>.
          This action cannot be undone.
        </p>
        <div className="flex gap-3 w-full mt-5">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[13px] font-manrope-semibold text-gray-600 hover:bg-gray-50 transition-all">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex-1 py-2.5 rounded-xl bg-primary text-white text-[13px] font-manrope-semibold hover:opacity-90 disabled:opacity-60 transition-all flex items-center justify-center gap-2"
          >
            {isDeleting && <Loader2 size={13} className="animate-spin" />}
            {isDeleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Add / edit road block modal ─── */
function RoadBlockFormModal({ block, onClose, onSaved }) {
  const editing = !!block;
  const [title, setTitle] = useState(block?.title || "");
  const [description, setDescription] = useState(block?.description || "");
  const [icon, setIcon] = useState(block?.icon || "");
  const [active, setActive] = useState(block?.active !== undefined ? block.active : true);
  const [location, setLocation] = useState(
    block?.location?.lat != null
      ? { lat: block.location.lat, lng: block.location.lng, address: "" }
      : null,
  );
  const [showMap, setShowMap] = useState(false);
  const [isUploadingIcon, setIsUploadingIcon] = useState(false);
  const [formError, setFormError] = useState("");
  const iconInputRef = useRef(null);

  const [uploadImages] = useUploadImagesMutation();
  const [createRoadBlock, { isLoading: isCreating }] = useCreateRoadBlockMutation();
  const [updateRoadBlock, { isLoading: isUpdating }] = useUpdateRoadBlockMutation();
  const isSaving = isCreating || isUpdating;

  const handleIconChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingIcon(true);
    setFormError("");
    try {
      const fd = new FormData();
      fd.append("images", file);
      const res = await uploadImages(fd).unwrap();
      const url = (res.data?.images || res.images || [])[0]?.url || "";
      if (!url) throw new Error("Upload returned no URL");
      setIcon(url);
    } catch (err) {
      setFormError(err?.data?.error?.message || "Icon upload failed.");
    } finally {
      setIsUploadingIcon(false);
      if (iconInputRef.current) iconInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    setFormError("");
    if (!title.trim()) { setFormError("Title is required."); return; }
    if (!location || location.lat == null || location.lng == null) {
      setFormError("Please pin the blocked location on the map.");
      return;
    }
    const payload = {
      title: title.trim(),
      description: description.trim(),
      icon,
      location: { lat: location.lat, lng: location.lng },
      active,
    };
    try {
      if (editing) {
        await updateRoadBlock({ id: block.id, ...payload }).unwrap();
      } else {
        await createRoadBlock(payload).unwrap();
      }
      onSaved(editing);
    } catch (err) {
      setFormError(err?.data?.error?.message || "Failed to save road block.");
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-[560px] max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
            <div>
              <h2 className="text-[15px] font-manrope-bold text-gray-900">
                {editing ? "Edit road block" : "Add road block"}
              </h2>
              <p className="text-[11px] font-manrope-regular text-gray-400 mt-0.5">
                Mark a closed road, works in progress, washed-out track, etc.
              </p>
            </div>
            <button onClick={onClose} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all">
              <X size={15} className="text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-4">
            {/* Location */}
            <div>
              <label className="block text-[12px] font-manrope-semibold text-gray-500 mb-1.5">Blocked location</label>
              <button
                type="button"
                onClick={() => setShowMap(true)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular hover:border-primary/40 transition-all text-left"
              >
                <span className={`truncate pr-2 ${location?.lat != null ? "text-gray-700" : "text-gray-400"}`}>
                  {location?.lat != null
                    ? (location.address || `${Number(location.lat).toFixed(5)}, ${Number(location.lng).toFixed(5)}`)
                    : "Pin on the map"}
                </span>
                <MapPin size={15} className="text-primary flex-shrink-0" strokeWidth={1.8} />
              </button>
              {location?.lat != null && (
                <p className="text-[10px] font-manrope-regular text-gray-400 mt-1 ml-1">
                  Lat: {Number(location.lat).toFixed(6)} · Lng: {Number(location.lng).toFixed(6)}
                </p>
              )}
            </div>

            {/* Title */}
            <div>
              <label className="block text-[12px] font-manrope-semibold text-gray-500 mb-1.5">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Kaya Grandi closed for roadworks"
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-[12px] font-manrope-semibold text-gray-500 mb-1.5">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Explain what's happening — detour advice, how long it lasts, etc. Shown to the user when they tap the icon."
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all resize-y"
              />
            </div>

            {/* Icon */}
            <div>
              <label className="block text-[12px] font-manrope-semibold text-gray-500 mb-1.5">
                Map icon <span className="text-gray-400 font-manrope-regular">(optional)</span>
              </label>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {icon ? (
                    <img src={icon} alt="" className="w-full h-full object-contain" />
                  ) : (
                    <AlertTriangle size={22} className="text-amber-500" strokeWidth={1.8} />
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <button
                    type="button"
                    onClick={() => !isUploadingIcon && iconInputRef.current?.click()}
                    disabled={isUploadingIcon}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-gray-200 hover:border-primary/40 hover:bg-gray-50 transition-all text-[12px] font-manrope-semibold text-gray-600"
                  >
                    {isUploadingIcon ? (
                      <><Loader2 size={13} className="animate-spin text-primary" /> Uploading…</>
                    ) : (
                      <><ImagePlus size={13} className="text-primary" /> {icon ? "Replace icon" : "Upload icon"}</>
                    )}
                  </button>
                  {icon && (
                    <button
                      type="button"
                      onClick={() => setIcon("")}
                      className="text-[11px] font-manrope-semibold text-gray-400 hover:text-red-500 transition-colors text-left"
                    >
                      Remove — use default warning icon
                    </button>
                  )}
                </div>
              </div>
              <p className="text-[10px] font-manrope-regular text-gray-400 mt-1.5 ml-1">
                PNG / SVG-style square image works best. Left blank, the app shows a standard warning icon.
              </p>
              <input
                ref={iconInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={handleIconChange}
              />
            </div>

            {/* Active toggle */}
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="w-4 h-4 accent-primary"
              />
              <span className="text-[12px] font-manrope-semibold text-gray-600">
                Visible in the app
              </span>
              <span className="text-[11px] font-manrope-regular text-gray-400">
                — uncheck to hide without deleting
              </span>
            </label>

            {formError && (
              <p className="text-[12px] font-manrope-regular text-red-500 bg-red-50 px-4 py-2.5 rounded-xl">
                {formError}
              </p>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0 bg-white">
            <button onClick={onClose} className="px-4 py-2 rounded-xl border border-gray-200 text-[12px] font-manrope-semibold text-gray-600 hover:bg-gray-50 transition-all">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || isUploadingIcon}
              className="px-4 py-2 rounded-xl bg-primary text-white text-[12px] font-manrope-semibold hover:opacity-90 disabled:opacity-40 transition-all flex items-center gap-2"
            >
              {isSaving && <Loader2 size={13} className="animate-spin" />}
              {isSaving ? "Saving…" : editing ? "Save changes" : "Add road block"}
            </button>
          </div>
        </div>
      </div>

      {showMap && (
        <LocationPickerModal
          initial={location}
          onClose={() => setShowMap(false)}
          onConfirm={(loc) => setLocation(loc)}
        />
      )}
    </>
  );
}

/* ─── Main page ─── */
export default function RoadBlocks() {
  const user = useSelector((state) => state.auth.user);
  const { data, isLoading, isError } = useGetRoadBlocksQuery();
  const [deleteRoadBlock, { isLoading: isDeleting }] = useDeleteRoadBlockMutation();
  const [updateRoadBlock] = useUpdateRoadBlockMutation();
  const toast = useToast();

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState("");

  const blocks = data?.data || [];
  const visible = search.trim()
    ? blocks.filter((b) =>
        `${b.title} ${b.description}`.toLowerCase().includes(search.toLowerCase()))
    : blocks;

  const handleDelete = async () => {
    try {
      await deleteRoadBlock(deleteTarget.id).unwrap();
      setDeleteTarget(null);
      toast.success("Road block deleted.");
    } catch (err) {
      toast.error(err?.data?.error?.message || "Failed to delete road block.");
      setDeleteTarget(null);
    }
  };

  const toggleActive = async (block) => {
    try {
      await updateRoadBlock({ id: block.id, active: !block.active }).unwrap();
      toast.success(block.active ? "Road block hidden." : "Road block is now visible.");
    } catch (err) {
      toast.error(err?.data?.error?.message || "Failed to update road block.");
    }
  };

  const ROW_GRID = "grid grid-cols-[48px_1.6fr_2fr_1fr_90px_90px] gap-3 px-5 items-center";

  return (
    <>
      <div className="flex flex-col min-h-screen">
        <header className="flex items-center justify-between px-8 pt-7 pb-4">
          <div>
            <h1 className="text-[22px] font-manrope-bold text-gray-900">Road Blocks</h1>
            <p className="text-[12px] font-manrope-regular text-gray-400 mt-0.5">
              Drop a marker on a closed or obstructed road. It shows on the tour map; tapping it
              opens your title and description.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-sm font-manrope-bold text-gray-600">
                {user?.name?.[0]?.toUpperCase() || "A"}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-[13px] font-manrope-semibold text-gray-900 leading-tight">{user?.name}</span>
              <span className="text-[11px] font-manrope-regular text-gray-400 leading-tight">{user?.email}</span>
            </div>
          </div>
        </header>

        <div className="flex-1 px-8 pb-8">
          <div className="bg-white rounded-2xl overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center gap-3 px-5 pt-5 pb-4 border-b border-gray-50">
              <div className="mr-auto">
                <h2 className="text-[15px] font-manrope-bold text-gray-900">All road blocks</h2>
                <p className="text-[11px] font-manrope-regular text-gray-400 mt-0.5">
                  {blocks.length} total · {blocks.filter((b) => b.active).length} visible in the app
                </p>
              </div>
              <div className="relative flex-1 max-w-xs">
                <input
                  type="text"
                  placeholder="Search road blocks"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-4 pr-4 py-2 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
                />
              </div>
              <button
                onClick={() => { setEditTarget(null); setFormOpen(true); }}
                className="flex items-center gap-2 px-4 h-9 bg-primary rounded-xl text-white text-[13px] font-manrope-semibold hover:opacity-90 transition-all flex-shrink-0"
              >
                <Plus size={15} strokeWidth={2.4} />
                Add road block
              </button>
            </div>

            {/* Table head */}
            <div className={`${ROW_GRID} py-3 border-b border-gray-50`}>
              <span className="text-[12px] font-manrope-semibold text-gray-400">Icon</span>
              <span className="text-[12px] font-manrope-semibold text-gray-400">Title</span>
              <span className="text-[12px] font-manrope-semibold text-gray-400">Description</span>
              <span className="text-[12px] font-manrope-semibold text-gray-400">Coordinates</span>
              <span className="text-[12px] font-manrope-semibold text-gray-400">Status</span>
              <span className="text-[12px] font-manrope-semibold text-gray-400">Action</span>
            </div>

            {isLoading && (
              <div className="flex items-center justify-center py-16 gap-2 text-gray-400">
                <Loader2 size={18} className="animate-spin" />
                <span className="text-[13px] font-manrope-regular">Loading road blocks…</span>
              </div>
            )}

            {isError && (
              <div className="flex items-center justify-center py-16 text-red-400 text-[13px] font-manrope-regular">
                Failed to load road blocks. Please try again.
              </div>
            )}

            {!isLoading && !isError && visible.length === 0 && (
              <div className="py-16 flex flex-col items-center justify-center gap-2">
                <AlertTriangle size={24} className="text-gray-300" strokeWidth={1.6} />
                <p className="text-[13px] font-manrope-semibold text-gray-400">
                  {search.trim() ? "No road blocks match your search" : "No road blocks yet"}
                </p>
              </div>
            )}

            {!isLoading && !isError && visible.length > 0 && (
              <div className="max-h-[620px] overflow-y-auto">
                {visible.map((b) => (
                  <div key={b.id} className={`${ROW_GRID} py-3.5 border-b border-gray-50 hover:bg-gray-50/50 transition-colors`}>
                    <div className="w-10 h-10 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center overflow-hidden">
                      {b.icon ? (
                        <img src={b.icon} alt="" className="w-full h-full object-contain" />
                      ) : (
                        <AlertTriangle size={18} className="text-amber-500" strokeWidth={1.8} />
                      )}
                    </div>
                    <p className="text-[13px] font-manrope-bold text-gray-800 truncate pr-2">{b.title}</p>
                    <p className="text-[12px] font-manrope-regular text-gray-500 line-clamp-2 pr-3">
                      {b.description || <span className="text-gray-300">—</span>}
                    </p>
                    <div>
                      <p className="text-[11px] font-manrope-regular text-gray-500">
                        {Number(b.location?.lat).toFixed(5)},
                      </p>
                      <p className="text-[11px] font-manrope-regular text-gray-500">
                        {Number(b.location?.lng).toFixed(5)}
                      </p>
                    </div>
                    <button
                      onClick={() => toggleActive(b)}
                      className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-manrope-semibold transition-all ${
                        b.active
                          ? "bg-green-50 text-green-600 hover:bg-green-100"
                          : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                      }`}
                      title={b.active ? "Visible — click to hide" : "Hidden — click to show"}
                    >
                      {b.active ? <Eye size={12} /> : <EyeOff size={12} />}
                      {b.active ? "Visible" : "Hidden"}
                    </button>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { setEditTarget(b); setFormOpen(true); }}
                        className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-all"
                        aria-label="Edit road block"
                      >
                        <Pencil size={13} className="text-gray-700" strokeWidth={1.8} />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(b)}
                        className="w-8 h-8 rounded-full bg-primary flex items-center justify-center hover:opacity-80 transition-all shadow-sm"
                        aria-label="Delete road block"
                      >
                        <Trash2 size={13} className="text-white" strokeWidth={1.8} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {formOpen && (
        <RoadBlockFormModal
          block={editTarget}
          onClose={() => { setFormOpen(false); setEditTarget(null); }}
          onSaved={(wasEditing) => {
            setFormOpen(false);
            setEditTarget(null);
            toast.success(wasEditing ? "Road block updated." : "Road block added.");
          }}
        />
      )}

      {deleteTarget && (
        <DeleteModal
          block={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
}
